import { create } from 'zustand';
import type { Category } from '@/features/categories/types';

interface CategoryState {
  selectedCategoryId: number | null;
  selectedCategory: Category | null;
  expandedCategories: number[];
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  selectedCategories: number[];
  editingCategory: Category | null;
  setSelectedCategory: (category: Category | null) => void;
  toggleExpanded: (categoryId: number) => void;
  openCreateModal: (parentId?: number) => void;
  closeCreateModal: () => void;
  openEditModal: (category: Category) => void;
  closeEditModal: () => void;
  openDeleteModal: (category: Category) => void;
  closeDeleteModal: () => void;
  toggleCategorySelection: (categoryId: number) => void;
  clearSelectedCategories: () => void;
  setSelectedCategories: (categoryIds: number[]) => void;
  setEditingCategory: (category: Category | null) => void;
}

export const useCategoryStore = create<CategoryState>((set) => ({
  // State
  selectedCategoryId: null,
  selectedCategory: null,
  expandedCategories: [],
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  selectedCategories: [],
  editingCategory: null,

  // Category Selection
  setSelectedCategory: (category) => set({ 
    selectedCategoryId: category?.id || null,
    selectedCategory: category 
  }),
  toggleExpanded: (categoryId) => set((state) => ({
    expandedCategories: state.expandedCategories.includes(categoryId)
      ? state.expandedCategories.filter(id => id !== categoryId)
      : [...state.expandedCategories, categoryId]
  })),

  // Modal Actions
  openCreateModal: (parentId) => set({
    isCreateModalOpen: true,
    selectedCategory: parentId ? { id: parentId } as Category : null
  }),
  closeCreateModal: () => set({ isCreateModalOpen: false, selectedCategory: null }),
  openEditModal: (category) => set({ isEditModalOpen: true, selectedCategory: category }),
  closeEditModal: () => set({ isEditModalOpen: false, selectedCategory: null }),
  openDeleteModal: (category) => set({ isDeleteModalOpen: true, selectedCategory: category }),
  closeDeleteModal: () => set({ isDeleteModalOpen: false, selectedCategory: null }),

  // Bulk Selection
  toggleCategorySelection: (categoryId) => set((state) => ({
    selectedCategories: state.selectedCategories.includes(categoryId)
      ? state.selectedCategories.filter(id => id !== categoryId)
      : [...state.selectedCategories, categoryId]
  })),
  clearSelectedCategories: () => set({ selectedCategories: [] }),
  setSelectedCategories: (categoryIds) => set({ selectedCategories: categoryIds }),

  // Editing
  setEditingCategory: (category) => set({ editingCategory: category }),
})); 